import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { toBlob, toPng } from 'html-to-image';

const { FiDownload, FiCopy, FiList, FiSend, FiCheck, FiAlertCircle } = FiIcons;

const ActionButtons = ({ boardRef, markers }) => {
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isProcessing, setIsProcessing] = useState(false);

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus({ type: '', message: '' }), 4000);
  };

  const getFormattedText = () => {
    if (markers.length === 0) return 'Geen notities.';
    return markers.map(m => `${m.number}. ${m.text || '(geen tekst)'}`).join('\n');
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(getFormattedText());
      showStatus('success', 'Lijst gekopieerd!');
    } catch (err) {
      showStatus('error', 'Kopiëren mislukt.');
    }
  };

  const handleCopyImage = async () => {
    if (!boardRef.current) return;
    setIsProcessing(true);
    try {
      // Small delay to ensure markers are rendered
      const blob = await toBlob(boardRef.current, { 
        cacheBust: false, // Set to false since we use base64
        pixelRatio: 2,
        backgroundColor: '#ffffff'
      });
      
      if (!blob) throw new Error('Canvas export mislukt');

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      showStatus('success', 'Afbeelding gekopieerd!');
    } catch (err) {
      console.error('Copy Image Error:', err);
      showStatus('error', 'Kon afbeelding niet kopiëren naar klembord.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!boardRef.current) return;
    setIsProcessing(true);
    try {
      const dataUrl = await toPng(boardRef.current, { 
        pixelRatio: 2,
        backgroundColor: '#ffffff'
      });
      const link = document.createElement('a');
      link.download = `annotatie-${new Date().getTime()}.png`;
      link.href = dataUrl;
      link.click();
      showStatus('success', 'PNG opgeslagen!');
    } catch (err) {
      console.error('Download Error:', err);
      showStatus('error', 'Downloaden mislukt.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWebhook = async () => {
    if (!boardRef.current) return;
    setIsProcessing(true);
    try {
      const blob = await toBlob(boardRef.current, { pixelRatio: 2, backgroundColor: '#ffffff' });
      if (!blob) throw new Error('Kon afbeelding niet genereren');

      const formData = new FormData();
      formData.append('file', blob, 'annotated-screenshot.png');
      formData.append('notes', getFormattedText());
      formData.append('timestamp', new Date().toISOString());

      const webhookUrl = import.meta.env.VITE_WEBHOOK_URL;
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        body: formData,
        mode: 'no-cors' // Use no-cors if the webhook doesn't return CORS headers
      });

      // Note: with no-cors, we can't read the response, but the request is sent
      showStatus('success', 'Verzonden naar Webhook!');
    } catch (err) {
      console.error('Webhook Error:', err);
      showStatus('error', 'Verzenden mislukt.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white border-t border-gray-200 p-4 shrink-0 shadow-[0_-10px_15px_-3px_rgba(0,0,0,0.05)]">
      
      {status.message && (
        <div className={`text-xs mb-3 px-3 py-2 rounded-lg flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 ${
          status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
        }`}>
          <SafeIcon icon={status.type === 'success' ? FiCheck : FiAlertCircle} />
          {status.message}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-2">
        <button 
          onClick={handleCopyImage}
          disabled={isProcessing}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-xl text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
        >
          <SafeIcon icon={FiCopy} />
          Afbeelding
        </button>
        <button 
          onClick={handleCopyText}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-xl text-sm font-semibold transition-all active:scale-95"
        >
          <SafeIcon icon={FiList} />
          Tekst
        </button>
      </div>

      <div className="space-y-2">
        <button 
          onClick={handleDownload}
          disabled={isProcessing}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-50 w-full shadow-sm"
        >
          <SafeIcon icon={FiDownload} />
          Download PNG
        </button>
        <button 
          idName="wphd-webhook-button"
          id="wphd-webhook-button"
          onClick={handleWebhook}
          disabled={isProcessing}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-50 w-full shadow-md"
        >
          <SafeIcon icon={FiSend} />
          {isProcessing ? 'Verwerken...' : 'Verstuur naar Webhook'}
        </button>
      </div>
    </div>
  );
};

export default ActionButtons;